import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { AppSlice } from '@src/store/reducers/AppSlice';

interface UseImprovedScrollDirectionOptions {
  /**
   * Minimum scroll distance before triggering direction change
   * @default 15
   */
  threshold?: number;
  /**
   * Minimum velocity (px/ms) to consider for momentum-based direction
   * @default 0.3
   */
  velocityThreshold?: number;
  /**
   * Throttle delay in milliseconds for state updates
   * @default 100 (prevents rapid state changes during fast scroll)
   */
  throttleDelay?: number;
  /**
   * Whether to enable momentum-based direction detection
   * @default true
   */
  enableMomentum?: boolean;
  /**
   * Whether to force show header when at top of page
   * @default true
   */
  showAtTop?: boolean;
  /**
   * Distance from top to consider as "at top"
   * @default 64
   */
  topThreshold?: number;
  /**
   * Whether to use force dispatch (bypasses locked state)
   * @default false
   */
  useForceDispatch?: boolean;
}

export const useImprovedScrollDirection = (options: UseImprovedScrollDirectionOptions = {}) => {
  const {
    threshold = 15,
    velocityThreshold = 0.3,
    throttleDelay = 100,
    enableMomentum = true,
    showAtTop = true,
    topThreshold = 64,
    useForceDispatch = false,
  } = options;

  const dispatch = useDispatch();
  const prevScrollTop = useRef(0);
  const lastScrollTime = useRef(Date.now());
  const velocity = useRef(0);
  const rafId = useRef<number | null>(null);
  const lastDispatchedValue = useRef<boolean | null>(null);
  const throttleTimeout = useRef<number | null>(null);
  const pendingValue = useRef<boolean | null>(null);
  const bounceScrollTimeout = useRef<number | null>(null);
  const isBouncing = useRef(false);
  const scrollHistory = useRef<number[]>([]);
  const directionChanges = useRef(0);

  const calculateVelocity = useCallback((currentScrollTop: number, currentTime: number) => {
    const timeDiff = currentTime - lastScrollTime.current;
    const scrollDiff = currentScrollTop - prevScrollTop.current;
    
    if (timeDiff > 0) {
      velocity.current = Math.abs(scrollDiff) / timeDiff;
    }
    
    return velocity.current;
  }, []);

  // Throttled dispatch function to prevent rapid state changes
  const throttledDispatch = useCallback((value: boolean) => {
    // Check if value actually changed
    if (lastDispatchedValue.current === value) {
      return;
    }
    
    // Store the pending value (always update to latest)
    pendingValue.current = value;
    
    // If we already have a pending dispatch, just update the value and return
    // The timeout will dispatch the latest value when it fires
    if (throttleTimeout.current !== null) {
      return;
    }
    
    // Dispatch immediately for the first change
    if (lastDispatchedValue.current === null) {
      if (useForceDispatch) {
        dispatch(AppSlice.actions.setDeliveryOptionsDownForce(value));
      } else {
        dispatch(AppSlice.actions.setDeliveryOptionsDown(value));
      }
      lastDispatchedValue.current = value;
      pendingValue.current = null;
      return;
    }
    
    // Throttle subsequent changes
    throttleTimeout.current = window.setTimeout(() => {
      const valueToDispatch = pendingValue.current;
      if (valueToDispatch !== null && valueToDispatch !== lastDispatchedValue.current) {
        if (useForceDispatch) {
          dispatch(AppSlice.actions.setDeliveryOptionsDownForce(valueToDispatch));
        } else {
          dispatch(AppSlice.actions.setDeliveryOptionsDown(valueToDispatch));
        }
        lastDispatchedValue.current = valueToDispatch;
      }
      pendingValue.current = null;
      throttleTimeout.current = null;
    }, throttleDelay);
  }, [useForceDispatch, dispatch, throttleDelay]);

  const handleScroll = useCallback(() => {
    if (rafId.current) {
      cancelAnimationFrame(rafId.current);
    }
    
    rafId.current = requestAnimationFrame(() => {
      const currentTime = Date.now();
      const scrollTop = document.documentElement.scrollTop;
      const scrollDiff = scrollTop - prevScrollTop.current;
      
      // Track scroll history for oscillation detection (last 5 values)
      scrollHistory.current.push(scrollTop);
      if (scrollHistory.current.length > 5) {
        scrollHistory.current.shift();
      }
      
      // Detect rapid direction changes (oscillation)
      if (scrollHistory.current.length >= 3) {
        const recent = scrollHistory.current.slice(-3);
        const changes = recent.reduce((acc, val, idx) => {
          if (idx > 0) {
            const diff = val - recent[idx - 1];
            const prevDiff = recent[idx - 1] - (recent[idx - 2] || recent[idx - 1]);
            // Count direction changes (positive to negative or vice versa)
            if ((diff > 0 && prevDiff < 0) || (diff < 0 && prevDiff > 0)) {
              return acc + 1;
            }
          }
          return acc;
        }, 0);
        directionChanges.current = changes;
      }
      
      // Detect iOS bounce scroll: scrollTop becomes negative or oscillates rapidly
      const isNegativeScroll = scrollTop < 0;
      const isNearTop = scrollTop <= topThreshold && scrollTop >= -50;
      const isRapidOscillation = 
        (Math.abs(scrollDiff) > 5 && isNearTop && Math.abs(scrollTop) < 20) ||
        (directionChanges.current >= 2 && scrollTop <= topThreshold * 2);
      
      // If we detect bounce scroll, lock the state to true and ignore changes
      if (isNegativeScroll || isRapidOscillation) {
        if (!isBouncing.current) {
          isBouncing.current = true;
          // Force show header during bounce
          throttledDispatch(true);
        }
        
        // Clear any existing timeout
        if (bounceScrollTimeout.current) {
          clearTimeout(bounceScrollTimeout.current);
        }
        
        // Reset bounce flag after bounce ends (check current scrollTop value)
        bounceScrollTimeout.current = window.setTimeout(() => {
          const currentScrollTop = document.documentElement.scrollTop;
          if (currentScrollTop >= 0 && currentScrollTop <= topThreshold) {
            isBouncing.current = false;
            throttledDispatch(true);
          }
        }, 150);
        
        prevScrollTop.current = scrollTop;
        lastScrollTime.current = currentTime;
        return;
      }
      
      // Reset bounce flag if we're no longer bouncing (scrollTop is positive and beyond threshold)
      if (isBouncing.current && scrollTop > topThreshold && scrollTop >= 0) {
        isBouncing.current = false;
        if (bounceScrollTimeout.current) {
          clearTimeout(bounceScrollTimeout.current);
          bounceScrollTimeout.current = null;
        }
      }
      
      // Ignore state changes during bounce scroll
      if (isBouncing.current) {
        return;
      }
      
      const isAtTop = scrollTop <= topThreshold;
      
      // Always show header when at top
      if (showAtTop && isAtTop) {
        throttledDispatch(true);
        prevScrollTop.current = scrollTop;
        lastScrollTime.current = currentTime;
        return;
      }
      
      // Only process if scroll distance exceeds threshold
      if (Math.abs(scrollDiff) < threshold) {
        return;
      }
      
      let isDown = true;
      
      if (enableMomentum) {
        const currentVelocity = calculateVelocity(scrollTop, currentTime);
        
        // Use velocity for momentum-based direction
        if (currentVelocity > velocityThreshold) {
          isDown = scrollDiff < 0; // Upward scroll shows header (isDown = true)
        } else {
          // Fallback to simple direction when velocity is low
          isDown = scrollDiff < 0;
        }
      } else {
        // Simple direction-based logic
        isDown = scrollDiff < 0; // Upward scroll shows header (isDown = true)
      }
      
      // Dispatch the state change (throttled)
      throttledDispatch(isDown);
      
      prevScrollTop.current = scrollTop;
      lastScrollTime.current = currentTime;
    });
  }, [
    threshold,
    velocityThreshold,
    enableMomentum,
    showAtTop,
    topThreshold,
    throttledDispatch,
    calculateVelocity,
  ]);

  useEffect(() => {
    // Initial call to set correct state
    handleScroll();
    
    // Add scroll listener with passive flag for better performance
    document.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      document.removeEventListener('scroll', handleScroll);
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
      }
      if (throttleTimeout.current !== null) {
        clearTimeout(throttleTimeout.current);
      }
      if (bounceScrollTimeout.current !== null) {
        clearTimeout(bounceScrollTimeout.current);
      }
    };
  }, [handleScroll]);

  return {
    // You can add any additional methods here if needed
  };
};

export default useImprovedScrollDirection;
